import React, { useEffect, useLayoutEffect, useState, useRef } from 'react'
import moment from 'moment'

import { useBanner } from '../../providers/BannerProvider/BannerProvider'

import {
  updateEventStatus,
  updatePackageStatus,
  updateFlexPackStatus,
} from '../../utilities/api'

import {
  formatDateTime,
  getFormattedTimezoneDate,
  getTimezoneDate,
  isISOString,
  globalStatuses,
} from '../../utilities/helpers'

import Stack from 'react-bootstrap/Stack'
import Dropdown from 'react-bootstrap/Dropdown'
import Button from 'react-bootstrap/Button'

import { BannerInfo } from '../BannerInfo'

import './banner.scss'

export default function Banner() {
  const {
    start,
    end,
    visibility,
    publishDate,
    setPublishDateLocal,
    isAfter,
    handlePublish,
    status,
    setStatus,
    setInitialState,
    setShow,
    setShowSchedule,
    isScheduled,
    setIsScheduled,
    obj,
    eventContext,
    eventPackageContext,
    flexPackContext,
    isEvent,
    isPackage,
    isPublishing,
    isPublished,
    isSoldout,
    canPublish,
    hasEnded,
    isOnsale,
    update,
  } = useBanner()

  const [showDropdownOpen, setShowDropdownOpen] = useState(false)

  useEffect(() => {
    // const date = publishDate

    // format publish date local to correct timezone only when date is in ISO format to diplay in date and time fields
    setPublishDateLocal(
      publishDate
        ? isISOString(publishDate)
          ? new Date(getFormattedTimezoneDate(publishDate, obj?.timezone))
          : publishDate
        : null
    )

    setIsScheduled(isAfter())

    getStatus(obj, publishDate)

    setInitialState({
      publishDate,
      isScheduled: isAfter(),
    })
  }, [
    publishDate,
    canPublish,
    isOnsale,
    isPublishing,
    isPublished,
    isSoldout,
    hasEnded,
    obj,
  ])

  const getStatus = async (obj, publishDate) => {
    // if obj has ended, status: ended - overrides any other status even hidden
    if (hasEnded) {
      setStatus('complete')

      // update status if is ended in context but not in db obj
      if (obj?.status) {
        if (getContext()?.status !== 'complete') {
          updateStatus(obj, 'complete', isEvent, isPackage)
        }
      }
    }
    // if obj has an isVisible property, status: hidden
    else if (obj?.hasOwnProperty('isVisible') && !obj?.isVisible) {
      setStatus('hidden')
    }

    // publishing - check status as well in case context (isPublishing) state isn't updated yet
    else if (isPublishing || getContext()?.status === 'publishing') {
      setStatus('publishing')
    }

    // if obj is published; four statuses: published, onsale, cancelled
    else if (isPublished || isOnsale) {
      // TODO: hook up cancelled status - published but offer dates have not passed ?

      // onsale: two statuses: onsale, sold out
      if (isOnsale) {
        // update status if is onsale in context but not in db obj
        if (obj?.status) {
          if (getContext()?.status !== 'on_sale') {
            updateStatus(obj, 'on_sale', isEvent, isPackage)
          }
        }
        // sold out
        if (isSoldout) {
          setStatus('sold_out')
        } else {
          // on sale
          setStatus('on_sale')
        }
      } else {
        // published
        setStatus('published')

        // is published
        if (obj?.status) {
          // update status if is published in context but not in db obj
          if (getContext()?.status !== 'published') {
            updateStatus(obj, 'published', isEvent, isPackage)
          }
        }
      }
      // obj is not published

      // not published; three statuses: draft, scheduled, will be published
    } else {
      // draft
      if (!publishDate) {
        setStatus('draft')
      }

      // scheduled
      // publish date is set to future date
      // or create flow not complete
      else if (isAfter() || !canPublish) {
        setStatus('scheduled')
      }

      // published
      // is before today's date
      else {
        // publishing is the default state when publishing
        setStatus('publishing')
        // if publish date changes or if canPublish changes, if not already published, publish event;
        if (!isPublished) handlePublish(publishDate)
      }
    }
  }

  // get context object
  // get object when calling updateStatus - obj (local state) does not update in time - is stale object
  // used for immediate access to context object
  const getContext = () => {
    return (
      (eventContext && Object.keys(eventContext).length > 0 && eventContext) ||
      (eventPackageContext &&
        Object.keys(eventPackageContext).length > 0 &&
        eventPackageContext) ||
      (flexPackContext &&
        Object.keys(flexPackContext).length > 0 &&
        flexPackContext)
    )
  }

  // Prevent duplicate status updates for the same object/status while a request is in-flight
  const pendingStatusUpdateRef = useRef({})
  const getTimezoneName = (format) => {
    const dateWithName = new Intl.DateTimeFormat('en-US', {
      timeZone: obj?.timezone?.utc[0],
      timeZoneName: format,
    }).format(new Date())

    if (format === 'long') {
      return dateWithName?.split(' ')?.slice(1)?.join(' ')
    } else {
      return dateWithName?.split(' ')?.pop()
    }
  }

  // if event is onsale in context from newly created offer but event is not onsale; change its status
  // if package is onsale in context but not in package object; change its status
  // if flex pack is onsale in context but not in flex pack object; change its status
  const updateStatus = async (obj, status, isEvent, isPackage) => {
    // guard: skip if the same status update for this object is already pending
    const key = obj?.uuid
    if (!key) return
    if (pendingStatusUpdateRef.current[key] === status) {
      // already updating to this status
      return
    }

    pendingStatusUpdateRef.current[key] = status

    try {
      let handler
      if (isEvent) {
        handler = updateEventStatus
      } else if (isPackage) {
        handler = updatePackageStatus
      } else {
        handler = updateFlexPackStatus
      }
      const data = {}
      data['id'] = obj?.uuid
      data['status'] = status
      const res = await handler({ data })
      update(res?.data)
    } catch (err) {
      console.error('Failed to update status', err)
    } finally {
      // clear pending flag
      if (pendingStatusUpdateRef.current[key] === status) {
        delete pendingStatusUpdateRef.current[key]
      }
    }
  }

  const handleShow = (action) => {
    setShowDropdownOpen(false)
    switch (action) {
      case 'publish':
        setShow(true)
        break

      case 'schedule':
        setShowSchedule(true)
        break

      default:
        break
    }
  }

  const forecast = obj?.weatherForecast
  const forecastDetails = [
    forecast?.conditionText,
    forecast?.precipitationProbability != null &&
      `${forecast.precipitationProbability}% chance of rain`,
    forecast?.windSpeed != null && `${forecast.windSpeed} mph wind`,
    forecast?.severeWeather && 'Severe weather',
  ].filter(Boolean)
  const showForecast =
    forecast && (forecast.temperature != null || forecastDetails.length > 0)
  const showDraftInventoryNotice = isEvent && status === 'draft'

  useLayoutEffect(() => {
    document.body.classList.toggle(
      'has-draft-inventory-notice',
      showDraftInventoryNotice
    )
    return () => {
      document.body.classList.remove('has-draft-inventory-notice')
    }
  }, [showDraftInventoryNotice])

  return (
    <>
      <div
        className={`banner banner--sticky${showDraftInventoryNotice ? ' banner--with-notice' : ''}`}
        id="banner"
      >
        <div className="banner-wrapper">
          <BannerInfo obj={obj} start={start} end={end} isEvent={isEvent} />
          <Stack
            direction="horizontal"
            gap={3}
            className="ms-auto align-items-center"
          >
            {showForecast && (
              <Stack
                direction="horizontal"
                gap={2}
                className="align-items-center border-end pe-3"
                data-testid="banner-weather"
              >
                {forecast.conditionIcon && (
                  <img
                    src={forecast.conditionIcon}
                    alt=""
                    width="45"
                    height="45"
                    className="flex-shrink-0"
                  />
                )}
                {forecast.temperature != null && (
                  <span className="small small-bold">
                    {forecast.temperature}°F
                  </span>
                )}
                {forecastDetails.length > 0 && (
                  <span className="small text-muted">
                    {forecastDetails.join(' · ')}
                  </span>
                )}
              </Stack>
            )}
            <Dropdown
              show={showDropdownOpen}
              id="status-dropdown"
              onToggle={() => setShowDropdownOpen(!showDropdownOpen)}
            >
              <Dropdown.Toggle
                variant="default"
                id="status-dropdown"
                className={`badge badge-lg badge--${globalStatuses[status]?.type}`}
              >
                {globalStatuses[status]?.label}
              </Dropdown.Toggle>
              {setShowDropdownOpen && (
                <Dropdown.Menu className="w-xl dropdown-menu-lg">
                  <span className="normal text-muted">
                    {isAfter()
                      ? 'Scheduled on'
                      : `${isEvent ? 'Event' : isPackage ? 'Package' : 'Flex package'} visibility`}
                    :
                  </span>
                  <h1 className="normal">
                    {visibility ? (
                      <>
                        <span>
                          {isISOString(visibility)
                            ? `${formatDateTime(getTimezoneDate(visibility, obj?.timezone), 'dateOnly')}`
                            : `${formatDateTime(moment(visibility), 'dateOnly')}`}
                        </span>
                        <span className="time">
                          {isISOString(visibility)
                            ? `${formatDateTime(getTimezoneDate(visibility, obj?.timezone), 'timeOnly')}`
                            : `${formatDateTime(moment(visibility), 'timeOnly')}`}
                        </span>
                      </>
                    ) : (
                      'As soon as published'
                    )}
                  </h1>
                  <span className="subtitle d-block mt-3">
                    Timezone: {getTimezoneName('long')} (
                    {getTimezoneName('short')})
                  </span>
                  {/* Stuck in publishing — allow organizer to re-enqueue inventory generation */}
                  {isEvent && status === 'publishing' && (
                    <Stack
                      direction="vertical"
                      className="btn-group-flex btn-group-flex-sm seperator seperator-sm"
                    >
                      <p className="mt-3 info-text info-text-md">
                        <span className="caption">
                          Inventory is still publishing. If this has been stuck,
                          retry to start again.
                        </span>
                      </p>
                      <Button
                        disabled={!canPublish || hasEnded}
                        onClick={() => {
                          setShowDropdownOpen(false)
                          handlePublish(publishDate, isScheduled, !isScheduled)
                        }}
                      >
                        Retry publish
                      </Button>
                    </Stack>
                  )}

                  {/* not visible yet (published) and event has not ended and event is not soldout */}
                  {status !== 'publishing' && !hasEnded && !isSoldout && (
                    <>
                      {!canPublish && (
                        <p className="mt-3 info-text info-text-md  info-text-danger">
                          <span className="caption text-danger">
                            {isEvent
                              ? 'Event'
                              : isPackage
                                ? 'Package'
                                : 'Flex package'}{' '}
                            {visibility ? 'will' : 'cannot'} be published{' '}
                            {visibility ? 'at scheduled time after' : 'without'}{' '}
                            completing{' '}
                            {isEvent
                              ? 'event details, event setting, and seat map'
                              : isPackage
                                ? 'package details, and seat map'
                                : 'flex package details'}
                          </span>
                        </p>
                      )}
                      {/* event/package/flex pack doesn't have visibility or is after today's date (draft or scheduled at later date) */}
                      {(!visibility || isScheduled || !canPublish) && (
                        <Stack
                          direction="horizontal"
                          className="btn-group-flex btn-group-flex-justify btn-group-flex-sm seperator seperator-sm"
                        >
                          {isEvent && (
                            <Button
                              variant="outline-light"
                              onClick={() => handleShow('schedule')}
                            >
                              {visibility ? 'Reschedule' : 'Schedule time'}
                            </Button>
                          )}
                          <Button
                            disabled={!canPublish || hasEnded}
                            onClick={() => handleShow('publish')}
                          >
                            Publish now
                          </Button>
                        </Stack>
                      )}
                    </>
                  )}
                </Dropdown.Menu>
              )}
            </Dropdown>
          </Stack>
        </div>
        {showDraftInventoryNotice && (
          <div className="banner-notice" data-testid="draft-inventory-notice">
            <p className="info-text info-text-md mb-0">
              Inventory won't be created until the event is published.
            </p>
          </div>
        )}
      </div>
    </>
  )
}
